/**
 * Quoten des Blocks „Im Raum" der Sektions-Detailseite (Audit-Paket B1, 30.10.2026). Rein, damit jest sie prüft.
 *
 * Anlass lSOJ3t: Der Raum erfasste 22 Zuschauer, die Teilnahme-Messung 155, die Player-Ereignisse 120 „Angebot gesehen" —
 * die Kachel zeigte „mind. 545 %". Regeln (Codex-Prüfung 5): KEIN Kappen auf 100 %. Jede Quote braucht einen gültigen
 * Nenner und Zähler, die aus derselben Messung stammen; sonst „—" mit Grund. Hat der Raum weniger als die Hälfte der
 * Teilnehmer erfasst oder liegt „gesehen" über den Zuschauern, ist die Raum-Messung des Termins unvollständig und der
 * Block zeigt keine Anteile, nur Anzahlen.
 */

const n = (v: number) => v.toLocaleString('de-DE');

/** Unter diesem Anteil der Teilnehmer gilt die Raum-Messung als unvollständig. */
export const RAUM_MIN_ANTEIL = 0.5;

export interface RaumQuote {
  /** Prozent (0–100) oder null, wenn die Quote nicht rechenbar ist. */
  wert: number | null;
  /** Grund für „—"; null bei rechenbarer Quote. */
  grund: string | null;
}

export interface RaumBewertung {
  /** Satz „Raum-Messung für diesen Termin unvollständig (…)", sonst null. */
  unvollstaendig: string | null;
  gesehen: RaumQuote;
  geklickt: RaumQuote;
  kauf: RaumQuote;
}

export interface RaumEingabe {
  /** Zuschauer laut Raum (`room.viewers`). */
  zuschauer: number;
  /** Teilnehmer laut Teilnahme-Messung; null, wenn die Teilnahme nicht messbar ist (dann keine Prüfung dagegen). */
  teilnehmer: number | null;
  /** „Angebot gesehen" (Personen, Untergrenze). */
  gesehen: number;
  /** „Angebot geklickt" (Personen). */
  klicks: number;
  /** Käufer unter den Klickern; null/undefined = nicht geliefert. */
  kaeufer?: number | null;
}

/** Unvollständige Raum-Messung erkennen: zu wenige erfasst oder mehr „gesehen" als Zuschauer. */
export function raumUnvollstaendig(p: Pick<RaumEingabe, 'zuschauer' | 'teilnehmer' | 'gesehen'>): string | null {
  if (p.teilnehmer !== null && p.teilnehmer > 0 && p.zuschauer < p.teilnehmer * RAUM_MIN_ANTEIL) {
    return `Raum-Messung für diesen Termin unvollständig (${n(p.zuschauer)} erfasst, ${n(p.teilnehmer)} laut Teilnahme)`;
  }
  if (p.gesehen > p.zuschauer) {
    return `Raum-Messung für diesen Termin unvollständig (${n(p.zuschauer)} erfasst, aber ${n(p.gesehen)} sahen das Angebot)`;
  }
  return null;
}

/** Quote nur mit gültigem Nenner und Zähler ≤ Nenner — nie über 100 %, nie gekappt. */
export function quote(zaehler: number | null | undefined, nenner: number | null | undefined, ohneNenner: string): RaumQuote {
  if (zaehler === null || zaehler === undefined) return { wert: null, grund: 'nicht gemessen' };
  if (nenner === null || nenner === undefined || !(nenner > 0)) return { wert: null, grund: ohneNenner };
  if (zaehler < 0 || zaehler > nenner) return { wert: null, grund: `${n(zaehler)} von ${n(nenner)} — die Zählungen passen nicht zusammen` };
  return { wert: (zaehler * 100) / nenner, grund: null };
}

export function raumBewertung(p: RaumEingabe): RaumBewertung {
  const unvollstaendig = raumUnvollstaendig(p);
  if (unvollstaendig) {
    const grund = 'kein Anteil, Raum-Messung unvollständig';
    const leer: RaumQuote = { wert: null, grund };
    return { unvollstaendig, gesehen: leer, geklickt: leer, kauf: leer };
  }
  return {
    unvollstaendig: null,
    gesehen: quote(p.gesehen, p.zuschauer, 'keine Zuschauer im Raum erfasst'),
    geklickt: quote(p.klicks, p.gesehen, 'niemand hat das Angebot gesehen'),
    kauf: quote(p.kaeufer, p.klicks, 'niemand hat auf das Angebot geklickt'),
  };
}
